// =====================================================
// Workout Module — TypeScript Types
// =====================================================

export type WorkoutLevel = 'beginner' | 'intermediate' | 'advanced';
export type WorkoutGoal = 'lose' | 'maintain' | 'gain';
export type WorkoutEmphasis =
  | 'balanced'
  | 'back_arms'
  | 'legs_glutes'
  | 'chest_shoulders'
  | 'upper'
  | 'lower';
export type MuscleGroup =
  | 'legs'
  | 'back'
  | 'chest'
  | 'shoulders'
  | 'arms'
  | 'core'
  | 'full_body';

// ─── Упражнение из базы ──────────────────────────────────────────────────────

export interface Exercise {
  id: string;
  name: string;
  category: string;                 // chest, back, shoulders, upper arms, ...
  equipment: string;               // body weight, dumbbell, barbell, cable, ...
  target: string | null;
  secondary_muscles: string[];
  instructions_en: string | null;
  instructions_ru: string | null;
  gif_url: string | null;
  image_url: string | null;
  muscle_group: MuscleGroup;
}

// ─── Тренировочный план ───────────────────────────────────────────────────────

export interface PlanExercise {
  exercise_id: string;
  sets: number;
  reps: string;                    // "10-12" или "до отказа"
  rest_seconds: number;
  notes?: string;                  // доп. инструкция ("контролируй опускание")
}

export interface PlanSession {
  day: number;                     // 1, 2, 3, 4
  name: string;                    // "Тренировка А"
  exercises: PlanExercise[];
  warmup_notes?: string;
  cooldown_notes?: string;
  scheduled_day?: number | null;   // 1=Пн, 2=Вт, ..., 7=Вс
}

export type CyclePhase = 'adaptation' | 'progression' | 'peak' | 'deload';

export function getCyclePhase(currentWeek: number): { phase: CyclePhase; label: string; emoji: string; color: string; desc: string } {
  const cycle = ((currentWeek - 1) % 4) + 1;
  const map = {
    1: { phase: 'adaptation' as CyclePhase, label: 'Адаптация',  emoji: '🌱', color: 'text-sky-400',       desc: 'Освоение движений, умеренная нагрузка' },
    2: { phase: 'progression' as CyclePhase, label: 'Прогрессия', emoji: '📈', color: 'text-emerald-400',   desc: 'Рабочие веса, нагрузка растёт' },
    3: { phase: 'peak' as CyclePhase,        label: 'Пик',        emoji: '🔥', color: 'text-amber-400',     desc: 'Максимальная интенсивность' },
    4: { phase: 'deload' as CyclePhase,      label: 'Дилоад',     emoji: '🔄', color: 'text-ink-dim',       desc: '−40% объёма, восстановление' },
  };
  return map[cycle as 1 | 2 | 3 | 4];
}

export interface PlanData {
  sessions: PlanSession[];
  methodology_notes?: string;      // общие методические заметки
  disclaimer: string;              // дисклеймер безопасности
}

export interface WorkoutPlan {
  id: string;
  user_id: string;
  name: string;
  goal: WorkoutGoal;
  level: WorkoutLevel;
  days_per_week: number;
  emphasis: WorkoutEmphasis;
  weekly_sets_per_group: number;
  is_active: boolean;
  current_week: number;
  equipment: string[];
  limitations: string | null;
  plan_data: PlanData | null;
  created_at: string;
  updated_at: string;
}

// ─── Сессия (выполненная тренировка) ─────────────────────────────────────────

export interface SetData {
  set: number;
  reps: number;
  weight_kg: number | null;        // null если упражнение без веса
  rpe?: number | null;             // Rate of Perceived Exertion, 6-10
  rir?: number | null;             // Reps in Reserve, 0-4
}

export interface ExerciseLog {
  id: string;
  session_id: string;
  user_id: string;
  exercise_id: string;
  exercise?: Exercise;             // join для отображения
  planned_sets: number | null;
  planned_reps: string | null;
  sets_data: SetData[];
  actual_sets: number;
  total_reps: number;
  total_tonnage_kg: number;
  estimated_1rm: number | null;
  position: number;
  created_at: string;
}

export interface WorkoutSession {
  id: string;
  user_id: string;
  plan_id: string | null;
  session_index: number | null;
  session_name: string | null;
  started_at: string;
  finished_at: string | null;
  duration_seconds: number | null;
  total_tonnage_kg: number;
  total_sets: number;
  exercises_completed: number;
  rating: 1 | 2 | 3 | 4 | 5 | null;
  notes: string | null;
  plan_week: number;
  created_at: string;
  exercise_logs?: ExerciseLog[];   // join для деталей
}

// ─── Рекорды силы ────────────────────────────────────────────────────────────

export interface StrengthHistoryEntry {
  date: string;
  estimated_1rm: number;
  weight_kg: number;
  reps: number;
}

export interface StrengthRecord {
  id: string;
  user_id: string;
  exercise_id: string;
  exercise?: Exercise;
  baseline_weight_kg: number | null;
  baseline_reps: number | null;
  baseline_1rm: number | null;
  baseline_set_at: string | null;
  best_weight_kg: number | null;
  best_reps: number | null;
  best_1rm: number | null;
  best_set_at: string | null;
  history: StrengthHistoryEntry[];
  updated_at: string;
}

// ─── Ачивки ───────────────────────────────────────────────────────────────────

export type WorkoutAchievementKey =
  | 'first_workout'
  | 'streak_3'
  | 'streak_5'
  | 'streak_10'
  | 'tonnage_1000'
  | 'tonnage_10000'
  | 'tonnage_100000'
  | 'pr_5pct'
  | 'pr_10pct'
  | 'pr_20pct'
  | 'record_day'
  | 'consistent_month';

export interface WorkoutAchievement {
  id: string;
  user_id: string;
  achievement_key: WorkoutAchievementKey;
  achievement_name: string;
  achievement_emoji: string | null;
  unlocked_at: string;
  share_data: {
    exercise?: string;
    old_1rm?: number;
    new_1rm?: number;
    percent?: number;
    tonnage?: number;
    streak?: number;
  } | null;
}

// ─── UI-состояние активной тренировки ────────────────────────────────────────

export interface ActiveSetState {
  reps: string;
  weight: string;
  done: boolean;
}

export interface ActiveExerciseState {
  exercise_id: string;
  sets: ActiveSetState[];
}

export interface ActiveWorkoutState {
  plan_id: string | null;
  session_index: number | null;
  session_name: string;
  started_at: Date;
  exercises: ActiveExerciseState[];
  current_exercise_index: number;
  rest_timer_seconds: number | null;
}

// ─── Опросник для создания плана ─────────────────────────────────────────────

export interface PlanQuestionnaire {
  days_per_week: number | 'optimal';
  level: WorkoutLevel;
  emphasis: WorkoutEmphasis;
  equipment: string[];
  limitations: string;
}

// ─── Группы мышц для UI (карта) ──────────────────────────────────────────────

export const MUSCLE_GROUP_META: Record<MuscleGroup, { label: string; emoji: string; color: string }> = {
  legs:      { label: 'Ноги',     emoji: '🦵', color: 'from-orange-500/20 to-orange-400/10' },
  back:      { label: 'Спина',    emoji: '🔙', color: 'from-blue-500/20 to-blue-400/10' },
  chest:     { label: 'Грудь',    emoji: '💪', color: 'from-red-500/20 to-red-400/10' },
  shoulders: { label: 'Плечи',    emoji: '🏋️', color: 'from-purple-500/20 to-purple-400/10' },
  arms:      { label: 'Руки',     emoji: '💪', color: 'from-yellow-500/20 to-yellow-400/10' },
  core:      { label: 'Пресс',    emoji: '⚡', color: 'from-emerald-500/20 to-emerald-400/10' },
  full_body: { label: 'Всё тело', emoji: '🌟', color: 'from-sky-500/20 to-sky-400/10' },
};

export const EMPHASIS_META: Record<WorkoutEmphasis, { label: string; description: string }> = {
  balanced:        { label: 'Сбалансированная нагрузка', description: 'Равномерная проработка всего тела' },
  back_arms:       { label: 'Акцент: Спина + руки',      description: 'Больше работы на спину и руки' },
  legs_glutes:     { label: 'Акцент: Ноги + ягодицы',    description: 'Больше работы на низ тела' },
  chest_shoulders: { label: 'Акцент: Грудь + плечи',     description: 'Больше работы на верх тела (пуш)' },
  upper:           { label: 'Акцент: Верх тела',         description: 'Грудь, спина, плечи, руки' },
  lower:           { label: 'Акцент: Низ тела',          description: 'Ноги, ягодицы, бёдра' },
};

export const LEVEL_META: Record<WorkoutLevel, { label: string; sets_per_group: number; description: string }> = {
  beginner:     { label: 'Новичок',      sets_per_group: 10, description: 'Менее 6 месяцев тренировок' },
  intermediate: { label: 'Бывалый',      sets_per_group: 14, description: '6 месяцев — 2 года тренировок' },
  advanced:     { label: 'Продвинутый',  sets_per_group: 18, description: 'Более 2 лет тренировок' },
};

export const RATING_META = [
  { value: 1 as const, emoji: '😫', label: 'Тяжело' },
  { value: 2 as const, emoji: '😕', label: 'Непросто' },
  { value: 3 as const, emoji: '😐', label: 'Нормально' },
  { value: 4 as const, emoji: '🙂', label: 'Хорошо' },
  { value: 5 as const, emoji: '💪', label: 'Отлично' },
];
